/// <reference path="./css.d.ts" />
import { Dialog } from '@base-ui/react/dialog';
import { CircleNotch, X, WarningCircle, ArrowClockwise, Tray } from '@phosphor-icons/react';
import { isValidElement } from 'react';
import type { ButtonHTMLAttributes, ComponentProps, ReactNode, Ref } from 'react';
import {
  Button as KitseedButton,
  type ButtonProps as KitseedButtonProps,
} from './kitseed/ui/button';
import { KitseedProvider } from './kitseed/lib/provider';
import { Tooltip, TooltipProvider } from './kitseed/ui/tooltip';
import * as Menu from './kitseed/ui/menu';
import {
  Field as KitseedField,
  Input as KitseedInput,
  Select as KitseedSelect,
  Textarea as KitseedTextarea,
} from './kitseed/ui/field';
import * as Settings from './kitseed/components/settings';
import { EmptyState as KitseedEmptyState, Alert } from './kitseed/components/states';
import { modalBackdropClassName, modalPopupMotionClassName } from './kitseed/ui/dialog';
export { SegmentedControl } from './kitseed/ui/segmented';
export { Badge } from './kitseed/ui/badge';
export { Kbd } from './kitseed/ui/kbd';
export { Skeleton } from './kitseed/ui/skeleton';
export { Panel } from './kitseed/ui/panel';
export { SearchField, FilterChip, SwitchField } from './kitseed/components/selection';
export { SidebarSection, SidebarItemContent } from './kitseed/components/navigation';
export { Avatar } from './kitseed/components/identity';
export { Tooltip, Alert };
export {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuGroup,
  MenuLabel,
  MenuItem,
  MenuCheckboxItem,
  MenuSubmenuRoot,
  MenuSubmenuTrigger,
  MenuSeparator,
} from './kitseed/ui/menu';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle' | 'danger-ghost';
  busy?: boolean;
  size?: 'sm' | 'md' | 'lg';
  render?: KitseedButtonProps['render'];
  ref?: Ref<HTMLButtonElement>;
};
export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  busy,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <KitseedButton
      unstyled
      {...props}
      data-size={size}
      className={`button button-${variant} ${className}`}
      aria-busy={busy || undefined}
      disabled={busy || disabled}
    >
      {busy ? <CircleNotch className="spin" size={16} aria-hidden="true" /> : null}
      {children}
    </KitseedButton>
  );
}
export function IconButton({
  label,
  children,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <Tooltip content={label}>
      <KitseedButton
        unstyled
        {...props}
        size="icon"
        aria-label={label}
        className={`icon-button ${className}`}
      >
        {children}
      </KitseedButton>
    </Tooltip>
  );
}
export function UIProvider({ children }: { children: ReactNode }) {
  return (
    <KitseedProvider density="compact">
      <TooltipProvider>{children}</TooltipProvider>
    </KitseedProvider>
  );
}
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className = '',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className={`modal-backdrop ${modalBackdropClassName}`} />
        <Dialog.Popup className={`modal ${modalPopupMotionClassName} ${className}`}>
          <div className="modal-heading">
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Close
              render={<button type="button" className="icon-button" aria-label="关闭" />}
            >
              <X size={18} />
            </Dialog.Close>
          </div>
          {description ? (
            <Dialog.Description className="muted modal-description">
              {description}
            </Dialog.Description>
          ) : null}
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
export function Dropdown({
  label,
  icon,
  children,
}: {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Menu.MenuRoot closeParentOnEsc>
      <Menu.MenuTrigger
        render={<button type="button" className="icon-button" aria-label={label} />}
      >
        {icon}
      </Menu.MenuTrigger>
      <Menu.MenuContent
        unstyled
        align="end"
        positionerStyle={{ zIndex: 100 }}
        className="menu-popup"
      >
        {children}
      </Menu.MenuContent>
    </Menu.MenuRoot>
  );
}
export function MenuAction({
  children,
  onClick,
  danger,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <Menu.MenuItem
      unstyled
      className={`menu-item${danger ? ' danger-text' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Menu.MenuItem>
  );
}
export function MenuDivider() {
  return <Menu.MenuSeparator className="menu-divider" />;
}
export function Field({ compound, children, ...props }: ComponentProps<typeof KitseedField>) {
  const control =
    isValidElement(children) &&
    [Input, Select, Textarea, KitseedInput, KitseedSelect, KitseedTextarea].includes(
      children.type as typeof Input,
    );
  return (
    <KitseedField {...props} compound={compound ?? !control}>
      {children}
    </KitseedField>
  );
}
export function Input(props: ComponentProps<typeof KitseedInput>) {
  return <KitseedInput unstyled {...props} />;
}
export function Select(props: ComponentProps<typeof KitseedSelect>) {
  return <KitseedSelect unstyled {...props} />;
}
export function Textarea(props: ComponentProps<typeof KitseedTextarea>) {
  return <KitseedTextarea unstyled {...props} />;
}
export function SettingsSection(props: ComponentProps<typeof Settings.SettingsSection>) {
  return (
    <Settings.SettingsSection
      unstyled
      {...props}
      slotProps={{
        header: { className: 'settings-section-header' },
        title: { className: 'settings-section-title' },
        description: { className: 'settings-section-description' },
        ...props.slotProps,
      }}
    />
  );
}
export function SettingsGroup({
  className = '',
  ...props
}: ComponentProps<typeof Settings.SettingsGroup>) {
  return <Settings.SettingsGroup unstyled {...props} className={`settings-panel ${className}`} />;
}
export function SettingsRow({
  className = '',
  ...props
}: ComponentProps<typeof Settings.SettingsRow>) {
  return (
    <Settings.SettingsRow
      unstyled
      {...props}
      className={`settings-row ${className}`}
      slotProps={{
        content: { className: 'grow' },
        title: { className: 'settings-row-title' },
        description: { className: 'settings-row-description' },
        actions: { className: 'settings-row-actions' },
        ...props.slotProps,
      }}
    />
  );
}
export function ErrorNotice({ error, retry }: { error: unknown; retry?: () => void }) {
  if (!error) return null;
  return (
    <Alert
      tone="danger"
      className="error-notice"
      icon={<WarningCircle size={18} />}
      action={
        retry ? (
          <button type="button" className="text-button" onClick={retry}>
            <ArrowClockwise size={14} />
            重试
          </button>
        ) : undefined
      }
    >
      <p>{error instanceof Error ? error.message : String(error)}</p>
    </Alert>
  );
}
export function EmptyState({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <KitseedEmptyState
      className="empty-state"
      title={title}
      description={description}
      icon={icon || <Tray size={23} />}
      action={action}
    />
  );
}
export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <span className="brand">
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect width="64" height="64" rx="16" fill="currentColor" />
        <path d="M21 16h7v15l12-15h9L34 34l16 15h-10L28 37v12h-7z" fill="var(--brand-fg)" />
      </svg>
      {compact ? null : (
        <span>
          kiipu<span className="brand-dot">.</span>
        </span>
      )}
    </span>
  );
}
export function LoadingScreen() {
  return (
    <div className="startup" role="status" aria-label="正在载入 Kiipu">
      <Brand compact />
      <span className="sr-only">正在载入工作区</span>
    </div>
  );
}
